import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosResponseHeaders, ResponseType } from "axios";
import { apiBaseUrls, EnvType, axiosForInterceptor, cacheService } from "./services";

export interface IApiClient {
  post<Trequest, Tresponse>(path: string, payload?: Trequest, abortSignal?: AbortSignal): Promise<Tresponse>;
  postUploadFormData<Tresponse>(
    path: string,
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<Tresponse>;
  patch<Trequest, Tresponse>(path: string, payload?: Trequest): Promise<Tresponse>;
  put<Trequest, Tresponse>(path: string, payload?: Trequest): Promise<Tresponse>;
  putUploadFormData<Tresponse>(
    path: string,
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<Tresponse>;
  get<Tresponse>(path: string): Promise<Tresponse>;
  getWithHeaders<Tresponse>(
    path: string,
    responseType: ResponseType | undefined
  ): Promise<[Tresponse, AxiosResponseHeaders]>;
  delete<Trequest, Tresponse>(path: string, payload?: Trequest): Promise<Tresponse>;
  client: AxiosInstance;
}

export default class ApiClient implements IApiClient {
  client: AxiosInstance;

  protected createAxiosClient(envType: EnvType): AxiosInstance {
    return Axios.create({
      baseURL: apiBaseUrls[envType],
      responseType: "json" as const,
    });
  }

  constructor(envType: EnvType) {
    this.client = this.createAxiosClient(envType);
    this.client.interceptors.response = axiosForInterceptor.interceptors.response;
  }

  async post<Trequest, Tresponse>(path: string, payload?: Trequest, abortSignal?: AbortSignal): Promise<Tresponse> {
    const headers: AxiosRequestConfig<Trequest> = {
      headers: {
        "Content-Type": "application/json",
        ...(cacheService.GetAuthKey() !== undefined && {
          Authorization: `Basic ${cacheService.GetAuthKey()}`,
        }),
      },
      signal: abortSignal,
    };

    const response = await this.client.post<Tresponse, AxiosResponse<Tresponse>, Trequest>(path, payload, headers);
    return response.data;
  }

  async postUploadFormData<Tresponse>(
    path: string,
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<Tresponse> {
    const config: AxiosRequestConfig<FormData> = {
      headers: {
        "Content-Type": "multipart/form-data", // Header really important here!
        ...(cacheService.GetAuthKey() !== undefined && {
          Authorization: `Basic ${cacheService.GetAuthKey()}`,
        }),
      },
      signal: abortSignal,
      onUploadProgress: onUploadProgressCallback,
    };

    const response = await this.client.post<Tresponse, AxiosResponse<Tresponse>, FormData>(path, formData, config);
    return response.data;
  }

  async patch<Trequest, Tresponse>(path: string, payload?: Trequest): Promise<Tresponse> {
    const headers = {
      headers: {
        "Content-Type": "application/json",
        ...(cacheService.GetAuthKey() !== undefined && {
          Authorization: `Basic ${cacheService.GetAuthKey()}`,
        }),
      },
    };
    const response = await this.client.patch<Tresponse, AxiosResponse<Tresponse>, Trequest>(path, payload, headers);
    return response.data;
  }

  async put<Trequest, Tresponse>(path: string, payload?: Trequest): Promise<Tresponse> {
    const headers = {
      headers: {
        "Content-Type": "application/json",
        ...(cacheService.GetAuthKey() !== undefined && {
          Authorization: `Basic ${cacheService.GetAuthKey()}`,
        }),
      },
    };
    const response = await this.client.put<Tresponse, AxiosResponse<Tresponse>, Trequest>(path, payload, headers);
    return response.data;
  }

  async putUploadFormData<Tresponse>(
    path: string,
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<Tresponse> {
    const config: AxiosRequestConfig<FormData> = {
      headers: {
        "Content-Type": "multipart/form-data", // Header really important here!
        ...(cacheService.GetAuthKey() !== undefined && {
          Authorization: `Basic ${cacheService.GetAuthKey()}`,
        }),
      },
      signal: abortSignal,
      onUploadProgress: onUploadProgressCallback,
    };

    const response = await this.client.putForm<Tresponse, AxiosResponse<Tresponse>, FormData>(path, formData, config);
    return response.data;
  }

  async get<Tresponse>(path: string): Promise<Tresponse> {
    const headers = {
      headers: {
        "Content-Type": "application/json",
        ...(cacheService.GetAuthKey() !== undefined && {
          Authorization: `Basic ${cacheService.GetAuthKey()}`,
        }),
      },
    };
    const response = await this.client.get<Tresponse, AxiosResponse<Tresponse>>(path, headers);
    return response.data;
  }

  async getWithHeaders<Tresponse>(
    path: string,
    responseType: ResponseType | undefined = undefined
  ): Promise<[Tresponse, AxiosResponseHeaders]> {
    const headers: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        ...(cacheService.GetAuthKey() !== undefined && {
          Authorization: `Basic ${cacheService.GetAuthKey()}`,
        }),
      },
      responseType: responseType,
    };
    const response = await this.client.get<Tresponse, AxiosResponse<Tresponse>>(path, headers);
    return [response.data, response.headers as AxiosResponseHeaders];
  }

  async delete<Trequest, Tresponse>(path: string, payload?: Trequest): Promise<Tresponse> {
    const headers = {
      "Content-Type": "application/json",
      ...(cacheService.GetAuthKey() !== undefined && {
        Authorization: `Basic ${cacheService.GetAuthKey()}`,
      }),
    };
    const response = await this.client.delete<Tresponse, AxiosResponse<Tresponse>, Trequest>(path, {
      data: payload,
      headers: headers,
    });
    return response.data;
  }
}
